// src/components/footer.js
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { Menu } from '@headlessui/react';
import { UserCircleIcon, HomeIcon, BellIcon, UsersIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useReminderCount } from '../hooks/useReminderCount';

const Footer = () => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const reminderCount = useReminderCount();

  // Helper for active state
  const isActive = (path) => location.pathname === path;

  // Five main nav items (Account/Profile is handled separately)
  const mobileNavItems = [
    { name: 'Community', icon: HomeIcon, path: '/my-community' },
    { name: 'Reminders', icon: BellIcon, path: '/reminders', badge: reminderCount > 0 },
    { name: 'Disciples', icon: UsersIcon, path: '/dashboard' },
    { name: 'Share Story', icon: PencilSquareIcon, path: '/share-story' },
  ];

  const adminMenuItems = [
    { name: 'Users', path: '/users' },
    { name: 'Groups', path: '/groups' },
    { name: 'Organizations', path: '/organizations' },
    { name: 'App Settings', path: '/app-admin' },
  ];

  const accountMenuItems = [
    { name: 'Your Profile', path: currentUser?.appID ? `/user/${currentUser.appID}` : '/profile' },
    { name: 'Training Modules', path: '/training-modules' },
    { name: 'Logout', isLogout: true },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Mobile Navigation Bar - Only visible on mobile when logged in */}
      {currentUser && <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex justify-around items-center h-16 px-2">
          {mobileNavItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`flex flex-col items-center justify-center px-2 py-1 relative ${
                isActive(item.path) ? 'text-blue-600' : 'text-gray-500'
              }`}
            >
              <item.icon className="h-6 w-6" />
              {/* Red dot for reminders */}
              {item.name === 'Reminders' && item.badge && (
                <span className="absolute top-1 right-3 h-2 w-2 bg-red-500 rounded-full"></span>
              )}
              <span className="text-xs mt-1 font-medium">{item.name}</span>
            </Link>
          ))}
          {/* Account/Profile button on far right */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex flex-col items-center justify-center text-gray-500 hover:text-blue-600 px-2 py-1">
              <UserCircleIcon className="h-6 w-6" />
              <span className="text-xs mt-1 font-medium">Account</span>
            </Menu.Button>
            <Menu.Items className="absolute bottom-16 right-0 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
              <div className="py-1">
                {accountMenuItems.map((item) =>
                  item.isLogout ? (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <button
                          onClick={logout}
                          className={`${active ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-700 w-full text-left`}
                        >
                          {item.name}
                        </button>
                      )}
                    </Menu.Item>
                  ) : (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <Link
                          to={item.path}
                          className={`${active ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-700`}
                        >
                          {item.name}
                        </Link>
                      )}
                    </Menu.Item>
                  )
                )}
                {/* Admin Section */}
                {currentUser?.isSuperAdmin && (
                  <>
                    <div className="border-t border-gray-200 my-1"></div>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500">Admin</div>
                    {adminMenuItems.map((item) => (
                      <Menu.Item key={item.name}>
                        {({ active }) => (
                          <Link
                            to={item.path}
                            className={`${active ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-700`}
                          >
                            {item.name}
                          </Link>
                        )}
                      </Menu.Item>
                    ))}
                  </>
                )}
              </div>
            </Menu.Items>
          </Menu>
        </div>
      </div>}

      {/* Desktop Footer - Only visible on desktop/tablet */}
      <div className="hidden md:block container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Logo Column */}
          <div className="flex flex-col">
            <Link to="/" className="text-white font-bold text-xl mb-4">
              Go Make Disciples
            </Link>
            <p className="text-sm text-gray-400 pr-4">
              Empowering spiritual mentors to guide and nurture disciples in their faith journey.
            </p>
          </div>

          {/* Links Column */}
          <div className="flex flex-col">
            <h3 className="text-white font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="hover:text-white transition duration-150">
                  About
                </Link>
              </li>
              <li>
                <Link to="/partners" className="hover:text-white transition duration-150">
                  Partners
                </Link>
              </li>
              <li>
                <a 
                  href="https://togetheronmission.org/blog/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition duration-150"
                >
                  Blog
                </a>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition duration-150">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition duration-150">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-white transition duration-150">
                  Terms of Use
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Links Column */}
          <div className="flex flex-col">
            <h3 className="text-white font-semibold text-lg mb-4">Connect With Us</h3>
            <ul className="space-y-2">
              <li>
                <a 
                  href="#" 
                  className="hover:text-white transition duration-150 flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                  X (Twitter)
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  className="hover:text-white transition duration-150 flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  Instagram
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  className="hover:text-white transition duration-150 flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
                  </svg>
                  Facebook
                </a>
              </li>
            </ul>
            <div className="mt-6">
              <a 
                href="https://togetheronmission.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 transition duration-150"
              >
                TogetherOnMission.org
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
